import { useState, useEffect, useCallback, useRef } from 'react';

interface VersionInfo {
    version: string;
    buildTime: string;
    buildTimestamp: number;
    gitCommit: string;
    environment: string;
}

interface UseVersionCheckerOptions {
    checkInterval?: number; // in milliseconds, default 5 minutes
    enabled?: boolean; // default true
    onUpdateDetected?: () => void;
}

interface UseVersionCheckerResult {
    updateAvailable: boolean;
    currentVersion: VersionInfo | null;
    latestVersion: VersionInfo | null;
    checkForUpdates: () => Promise<void>;
    dismissUpdate: () => void;
    refreshApp: () => void;
}

export const useVersionChecker = (
    options: UseVersionCheckerOptions = {}
): UseVersionCheckerResult => {
    const { checkInterval = 5 * 60 * 1000, enabled = true, onUpdateDetected } = options; // Default 5 minutes

    const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
    const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout>();
    const dismissedVersionRef = useRef<number | null>(null);
    const onUpdateDetectedRef = useRef(onUpdateDetected);
    const currentVersionRef = useRef<VersionInfo | null>(null);

    // Keep callback refs in sync without retriggering polling effects
    useEffect(() => {
        onUpdateDetectedRef.current = onUpdateDetected;
    }, [onUpdateDetected]);

    // Keep currentVersion ref in sync with state
    useEffect(() => {
        currentVersionRef.current = currentVersion;
    }, [currentVersion]);

    // Fetch version info from server
    const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
        try {
            // Add cache-busting parameter to ensure fresh data
            const response = await fetch(`/version.json?t=${Date.now()}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                },
            });

            if (!response.ok) {
                console.warn('Failed to fetch version info:', response.statusText);
                return null;
            }

            const versionInfo: VersionInfo = await response.json();
            return versionInfo;
        } catch (error) {
            console.error('Error fetching version info:', error);
            return null;
        }
    }, []);

    // Check for updates - use refs to keep this callback stable
    const checkForUpdates = useCallback(async () => {
        if (!enabled) return;

        const latest = await fetchVersion();
        if (!latest) return;

        // Set latest version only when it actually changes to avoid unnecessary re-renders
        setLatestVersion((prev) => {
            if (prev && prev.buildTimestamp === latest.buildTimestamp) {
                return prev;
            }
            return latest;
        });

        // If this is the first check, set current version
        if (!currentVersionRef.current) {
            setCurrentVersion(latest);
            console.log('📦 Current app version:', latest);
            return;
        }

        // Check if there's a new version
        const isNewVersion = latest.buildTimestamp > currentVersionRef.current.buildTimestamp;

        // Don't show update if user dismissed this specific version
        const isDismissed = dismissedVersionRef.current === latest.buildTimestamp;

        if (isNewVersion && !isDismissed) {
            console.log('🚀 New version detected!');
            console.log('Current:', currentVersionRef.current);
            console.log('Latest:', latest);
            setUpdateAvailable(true);
            onUpdateDetectedRef.current?.();
        }
    }, [enabled, fetchVersion]);

    // Dismiss update notification
    const dismissUpdate = useCallback(() => {
        if (latestVersion) {
            dismissedVersionRef.current = latestVersion.buildTimestamp;
            setUpdateAvailable(false);
            console.log('Update notification dismissed for version:', latestVersion.buildTimestamp);
        }
    }, [latestVersion]);

    // Hard refresh the app (equivalent to Ctrl+F5)
    const refreshApp = useCallback(() => {
        // Clear all caches and reload
        if ('caches' in window) {
            caches.keys().then((names) => {
                names.forEach((name) => {
                    caches.delete(name);
                });
            });
        }

        // Clear session storage (optional, keep localStorage for user data)
        sessionStorage.clear();

        // Perform hard reload
        window.location.reload();
    }, []);

    // Set up periodic version checking
    useEffect(() => {
        if (!enabled) {
            console.log('🔧 Version checker disabled');
            return;
        }

        // Clear any existing interval first
        if (intervalRef.current) {
            console.log('⚠️ Clearing existing interval before creating new one');
            clearInterval(intervalRef.current);
        }

        console.log(`🔧 Setting up version check interval (every ${checkInterval / 1000 / 60} minutes)`);

        // Initial check
        checkForUpdates();

        // Set up interval for periodic checks
        intervalRef.current = setInterval(() => {
            checkForUpdates();
        }, checkInterval);

        // Cleanup interval on unmount or when dependencies change
        return () => {
            if (intervalRef.current) {
                console.log('🧹 Cleaning up version check interval');
                clearInterval(intervalRef.current);
                intervalRef.current = undefined;
            }
        };
    }, [enabled, checkInterval, checkForUpdates]);

    // Also check when tab becomes visible again (with throttling)
    useEffect(() => {
        if (!enabled) return;

        let lastCheckTime = 0;
        const THROTTLE_MS = 10000; // Don't check more than once every 10 seconds

        const handleVisibilityChange = () => {
            if (!document.hidden) {
                const now = Date.now();
                if (now - lastCheckTime > THROTTLE_MS) {
                    console.log('👁️ Tab visible, checking for updates...');
                    lastCheckTime = now;
                    checkForUpdates();
                } else {
                    console.log('👁️ Tab visible but throttled (checked recently)');
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [enabled, checkForUpdates]);

    return {
        updateAvailable,
        currentVersion,
        latestVersion,
        checkForUpdates,
        dismissUpdate,
        refreshApp,
    };
};
